import router from "@/router";
import { useRouteStore } from "@/stores/routeStore";

/**
 * 动态加载组件
 * @param {string} componentPath - 组件路径
 * @returns {function(): Promise<*>} - 异步加载的组件
 */
const loadComponent = (componentPath) => {
  return () => import(`@/views/${componentPath}.vue`);
};

/**
 * 添加路由到路由器和 Pinia 状态管理
 * @param {Object} newRoute - 新路由对象
 * @param {string} componentPath - 组件路径
 */
const addRouteToRouterAndStore = (newRoute, componentPath) => {
  // 动态加载组件
  newRoute.component = loadComponent(componentPath);

  // 如果路由不存在，则添加到路由器
  if (!router.hasRoute(newRoute.name)) {
    router.addRoute(newRoute);
  }

  // 如果路由不存在，则添加到 Pinia 状态管理
  const routeStore = useRouteStore();
  if (!routeStore.hasRoute(newRoute.name)) {
    routeStore.addRoute(newRoute);
  }
};

/**
 * 从 sessionStorage 中获取动态路由
 * @returns {Array} - 动态路由列表
 */
const getDynamicRoutesFromSessionStorage = () => {
  return JSON.parse(sessionStorage.getItem("dynamicRoutes") || "[]");
};

/**
 * 添加路由到 sessionStorage
 * @param {Object} newRoute - 新路由对象
 * @param {string} componentPath - 组件路径
 */
const addRouteToSessionStorage = (newRoute, componentPath) => {
  const dynamicRoutes = getDynamicRoutesFromSessionStorage();

  // 检查路由是否已存在
  const isRouteExist = dynamicRoutes.some(
    (route) => route.name === newRoute.name
  );

  // 如果不存在，则添加到 sessionStorage
  if (!isRouteExist) {
    dynamicRoutes.push({
      path: newRoute.path,
      name: newRoute.name,
      meta: newRoute.meta,
      componentPath: componentPath,
    });
    sessionStorage.setItem("dynamicRoutes", JSON.stringify(dynamicRoutes));
  }
};

/**
 * 添加路由（如果不存在）
 * @param {Object} newRoute - 新路由对象
 * @param {string} componentPath - 组件路径
 */
export const addRouteIfNotExist = (newRoute, componentPath) => {
  addRouteToRouterAndStore(newRoute, componentPath);
  addRouteToSessionStorage(newRoute, componentPath);
};

/**
 * 从 sessionStorage 中重新加载路由
 */
export const reloadRoutesFromSessionStorage = () => {
  const dynamicRoutes = getDynamicRoutesFromSessionStorage();

  dynamicRoutes.forEach((route) => {
    const newRoute = {
      path: route.path,
      name: route.name,
      meta: route.meta,
    };
    addRouteToRouterAndStore(newRoute, route.componentPath);
  });
};
